import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { fetchWorkflowLists, getWorkflowListsQueryKey } from "@repo/shared-components";
import { configurationApi } from "~/api";
import type { Task, Item, Status, List } from "~/types/api";
import { fetchStatuses, getStatusesQueryKey } from "../../../hooks/apis/use-statuses";
import { fetchTaskItems, getTaskItemsQueryKey } from "../../../hooks/apis/use-task-items";

export const useWindowDataLoader = () => {
  const queryClient = useQueryClient();

  const fetchListForWorkflow = useCallback(
    async (workflowIds: number[]): Promise<List[]> => {
      const uniqueWorkflowIds = Array.from(new Set(workflowIds));
      const listPromises = uniqueWorkflowIds.map((wId) =>
        queryClient.fetchQuery({
          queryKey: getWorkflowListsQueryKey(configurationApi, wId),
          queryFn: () => fetchWorkflowLists(configurationApi, wId),
          staleTime: 10 * 60 * 1000,
          meta: { errorTitle: `Failed to load lists for workflow ${wId}` },
        })
      );
      const listsResponses = await Promise.all(listPromises);
      return listsResponses.flat();
    },
    [queryClient]
  );

  const fetchTaskWindowData = useCallback(
    async (tasks: Task[]): Promise<{ items: Item[]; statuses: Status[] }> => {
      const taskWorkflowIds = Array.from(new Set(tasks.map((t) => t.workflowId)));
      const taskStatusPromises = taskWorkflowIds.map((wId) =>
        queryClient.fetchQuery({
          queryKey: getStatusesQueryKey(wId),
          queryFn: () => fetchStatuses(wId),
          staleTime: 10 * 60 * 1000,
        })
      );

      const itemsPromises = tasks.map((t) =>
        queryClient.fetchQuery({
          queryKey: getTaskItemsQueryKey(t.id),
          queryFn: () => fetchTaskItems(t.id),
          staleTime: 5 * 60 * 1000,
          meta: { errorTitle: `Failed to load items for task ${t.name}` },
        })
      );

      const [itemsResponses, taskStatuses] = await Promise.all([
        Promise.all(itemsPromises),
        Promise.all(taskStatusPromises),
      ]);
      const items = itemsResponses.flat();

      const additionalWorkflowIds = new Set<number>();
      for (const item of items) {
        if (item.workflowId && !taskWorkflowIds.includes(item.workflowId)) {
          additionalWorkflowIds.add(item.workflowId);
        }
      }

      let additionalStatuses: Status[] = [];
      if (additionalWorkflowIds.size > 0) {
        const additionalPromises = Array.from(additionalWorkflowIds).map((wId) =>
          queryClient.fetchQuery({
            queryKey: getStatusesQueryKey(wId),
            queryFn: () => fetchStatuses(wId),
            staleTime: 10 * 60 * 1000,
          })
        );
        additionalStatuses = (await Promise.all(additionalPromises)).flat();
      }

      return {
        items,
        statuses: [...taskStatuses.flat(), ...additionalStatuses],
      };
    },
    [queryClient]
  );

  return {
    fetchTaskWindowData,
    fetchListForWorkflow,
  };
};
